import bindAll from 'lodash.bindall';
import React from 'react';
import PropTypes from 'prop-types';
import {defineMessages, injectIntl} from 'react-intl';
import intlShape from './intlShape';
import {connect} from 'react-redux';

import log from '../lib/log';
import sharedMessages from './shared-messages';
import useFirebaseLoader from '../components/gui/firebaseLoader';

import {
    LoadingStates,
    getIsLoadingUpload,
    getIsShowingWithoutId,
    onLoadedProject,
    requestProjectUpload
} from '../reducers/project-state';

import {setProjectTitle} from '../reducers/project-title';

import {
    openLoadingProject,
    closeLoadingProject
} from '../reducers/modals';

import {getProjectTitleFromFilename} from './sb-file-uploader-utils';

const messages = defineMessages({
    loadError: {
        id: 'gui.projectLoader.loadError',
        defaultMessage: 'The project file that was selected failed to load.',
        description: 'An error that displays when a local project file fails to load.'
    }
});

const SBFileUploaderHOC = function (WrappedComponent) {
    const FirebaseAutoUploader = ({startUpload, fileSelected}) => {
        useFirebaseLoader(startUpload, fileSelected);
        return null;
    };

    FirebaseAutoUploader.propTypes = {
        startUpload: PropTypes.func.isRequired,
        fileSelected: PropTypes.func.isRequired
    };

    class SBFileUploaderComponent extends React.Component {
        constructor (props) {
            super(props);

            bindAll(this, [
                'createFileObjects',
                'handleFinishedLoadingUpload',
                'handleStartSelectingFileUpload',
                'handleChange',
                'onload',
                'removeFileObjects'
            ]);

            this.loadingState = null;
            this.isFirebaseInjected = false;
        }

        componentDidUpdate (prevProps) {
            if (this.props.isLoadingUpload && !prevProps.isLoadingUpload) {
                this.handleFinishedLoadingUpload();
            }
        }

        componentWillUnmount () {
            this.removeFileObjects();
        }

        handleStartSelectingFileUpload () {
            this.createFileObjects();
        }

        createFileObjects () {
            this.removeFileObjects();

            this.fileReader = new FileReader();
            this.fileReader.onload = this.onload;

            this.inputElement = document.createElement('input');
            this.inputElement.accept = '.sb,.sb2,.sb3';
            this.inputElement.style = 'display: none;';
            this.inputElement.type = 'file';
            this.inputElement.onchange = this.handleChange;

            document.body.appendChild(this.inputElement);

            this.inputElement.click();
        }

        handleChange (e) {
            const {
                intl,
                isShowingWithoutId,
                loadingState,
                projectChanged,
                userOwnsProject
            } = this.props;

            const thisFileInput = e.target;

            if (!thisFileInput.files || !thisFileInput.files.length) {
                return;
            }

            this.fileToUpload = thisFileInput.files[0];

            const isAutoInjected =
                thisFileInput.dataset?.source === 'firebase-loader';

            if (this.fileReader && this.fileReader.readyState === 1) {
                this.fileReader.abort();
            }

            /*
             * Firebase viewer load.
             *
             * This load does not use Scratch's normal requestProjectUpload
             * state transition. The Firebase loader injects the file directly
             * and we load it directly into the VM.
             */
            if (isAutoInjected) {
                console.log('[UPLOAD] Firebase project injected into Scratch');

                

                this.isFirebaseInjected = true;
                this.loadingState = null;

                this.fileReader.readAsArrayBuffer(this.fileToUpload);

                return;
            }

            /*
             * Normal local Scratch upload.
             */
            let uploadAllowed = true;

            if (userOwnsProject || (projectChanged && isShowingWithoutId)) {
                uploadAllowed = confirm( // eslint-disable-line no-alert
                    intl.formatMessage(sharedMessages.replaceProjectWarning)
                );
            }

            if (uploadAllowed) {
                this.isFirebaseInjected = false;
                this.loadingState = loadingState;

                this.props.requestProjectUpload(loadingState);
            } else {
                this.removeFileObjects();
            }
        }

        handleFinishedLoadingUpload () {
            if (this.fileToUpload && this.fileReader) {
                if (this.fileReader.readyState !== 1) {
                    this.fileReader.readAsArrayBuffer(this.fileToUpload);
                }
            } else {
                console.warn('[Uploader] Missing fileToUpload or fileReader');

                if (this.props.loadingState) {
                    this.props.cancelFileUpload(this.props.loadingState);
                }

                this.removeFileObjects();
            }
        }

        onload () {
            if (!this.fileReader) {
                return;
            }

            /*
             * Only normal local uploads use Scratch's loading modal.
             * Firebase viewer loading has its own outer loading screen.
             */
            if (!this.isFirebaseInjected) {
                this.props.onLoadingStarted();
            }

            const filename =
                this.fileToUpload && this.fileToUpload.name;

            const isFirebaseInjected = this.isFirebaseInjected;

            let loadingSuccess = false;

            this.props.vm.loadProject(this.fileReader.result)
                .then(() => {
                    if (filename) {
                        const uploadedProjectTitle =
                            getProjectTitleFromFilename(filename);

                        this.props.onSetProjectTitle(uploadedProjectTitle);
                    }

                   loadingSuccess = true;

console.log('[UPLOAD] Scratch project loaded into VM');

/*
 * Check whether the loaded purchased project contains Scratch blocks.
 */
if (isFirebaseInjected) {
    const targets =
        this.props.vm.runtime?.targets || [];

    const targetResults = targets.map(target => {
        const blocks =
            target.blocks?._blocks || {};

        const blockCount =
            Object.keys(blocks).length;

        console.log(
            `[ALYD CODE CHECK AFTER LOAD] ${
                target.getName ?
                    target.getName() :
                    'Unknown'
            }: ${blockCount} blocks`
        );

        return blockCount > 0;
    });

    const hasCode =
        targetResults.some(Boolean);

        window.dispatchEvent(
        new CustomEvent('alyd-purchased-project-code-check', {
            detail: {
                hasCode
            }
        })
    );

    console.log(
        `[ALYD CODE CHECK AFTER LOAD] PROJECT HAS CODE: ${hasCode}`
    );
}
                    
                })
                
                .catch(error => {
                    log.warn(error);

                    alert( // eslint-disable-line no-alert
                        this.props.intl.formatMessage(messages.loadError)
                    );
                })
                .then(() => {
                    /*
                     * Firebase did not enter Scratch's normal upload state,
                     * so do not call onLoadedProject for that path.
                     */
                 this.props.onLoadingFinished(
                this.props.loadingState,
                loadingSuccess,
                isFirebaseInjected
            );

                    this.removeFileObjects();
                });
        }

        removeFileObjects () {
            if (this.inputElement) {
                this.inputElement.value = null;

                if (this.inputElement.parentNode) {
                    this.inputElement.parentNode.removeChild(
                        this.inputElement
                    );
                }
            }

            this.inputElement = null;
            this.fileReader = null;
            this.fileToUpload = null;
            this.loadingState = null;
            this.isFirebaseInjected = false;
        }

        render () {
            const {
                /* eslint-disable no-unused-vars */
                cancelFileUpload,
                isLoadingUpload,
                isShowingWithoutId,
                loadingState,
                onLoadingFinished,
                onLoadingStarted,
                onSetProjectTitle,
                projectChanged,
                requestProjectUpload: requestProjectUploadProp,
                /* eslint-enable no-unused-vars */

                ...componentProps
            } = this.props;

            return (
                <React.Fragment>
                    <FirebaseAutoUploader
                        startUpload={this.handleStartSelectingFileUpload}
                        fileSelected={this.handleChange}
                    />

                    <WrappedComponent
                        onStartSelectingFileUpload={this.handleStartSelectingFileUpload}
                        {...componentProps}
                    />
                </React.Fragment>
            );
        }
    }

    SBFileUploaderComponent.propTypes = {
        canSave: PropTypes.bool,
        cancelFileUpload: PropTypes.func,
        intl: intlShape.isRequired,
        isLoadingUpload: PropTypes.bool,
        isShowingWithoutId: PropTypes.bool,
        loadingState: PropTypes.oneOf(LoadingStates),
        onLoadingFinished: PropTypes.func,
        onLoadingStarted: PropTypes.func,
        onSetProjectTitle: PropTypes.func,
        projectChanged: PropTypes.bool,
        requestProjectUpload: PropTypes.func,
        userOwnsProject: PropTypes.bool,
        vm: PropTypes.shape({
            loadProject: PropTypes.func
        })
    };

    const mapStateToProps = (state, ownProps) => {
        const loadingState =
            state.scratchGui.projectState.loadingState;

        const user =
            state.session &&
            state.session.session &&
            state.session.session.user;

        return {
            isLoadingUpload: getIsLoadingUpload(loadingState),
            isShowingWithoutId: getIsShowingWithoutId(loadingState),
            loadingState: loadingState,
            projectChanged: state.scratchGui.projectChanged,

            userOwnsProject: ownProps.userOwnsProject ?? (
                ownProps.authorUsername &&
                user &&
                ownProps.authorUsername === user.username
            ),

            vm: state.scratchGui.vm
        };
    };

   const mapDispatchToProps = (dispatch, ownProps) => ({
    cancelFileUpload: loadingState => {
        if (!loadingState) {
            console.warn(
                '[Uploader] Skipped cancelFileUpload: missing loadingState'
            );

            dispatch(closeLoadingProject());

            return;
        }

        try {
            const action = onLoadedProject(
                loadingState,
                false,
                false
            );

            console.log('[UPLOAD CANCEL DEBUG]', {
                loadingState,
                action
            });

            if (action) {
                dispatch(action);
            } else {
                console.error(
                    '[UPLOAD CANCEL DEBUG] onLoadedProject returned undefined',
                    {
                        loadingState
                    }
                );
            }
        } catch (err) {
            console.error(
                '[Uploader] cancelFileUpload failed:',
                err
            );
        }

        dispatch(closeLoadingProject());
    },

    onLoadingFinished: (loadingState, success, isFirebaseInjected) => {
        /*
         * Firebase viewer loads bypass requestProjectUpload().
         * Therefore there is no Scratch upload state to complete.
         */
        if (isFirebaseInjected) {
            console.log(
                '[UPLOAD] Firebase viewer project finished loading'
            );

            dispatch(closeLoadingProject());

            return;
        }

        /*
         * Normal local uploads continue through modern Scratch's
         * normal project-state completion path.
         */
        if (!loadingState) {
            console.warn(
                '[Uploader] Skipped onLoadedProject: missing loadingState'
            );

            dispatch(closeLoadingProject());

            return;
        }

        try {
            const action = onLoadedProject(
                loadingState,
                ownProps.canSave,
                success
            );

            console.log('[UPLOAD STATE DEBUG]', {
                loadingState,
                canSave: ownProps.canSave,
                success,
                action
            });

            if (action) {
                dispatch(action);
            } else {
                console.error(
                    '[UPLOAD STATE DEBUG] onLoadedProject returned undefined',
                    {
                        loadingState,
                        canSave: ownProps.canSave,
                        success
                    }
                );
            }
        } catch (err) {
            console.error(
                '[Uploader] onLoadedProject failed:',
                err
            );
        }

        dispatch(closeLoadingProject());
    },

    onLoadingStarted: () => {
        dispatch(openLoadingProject());
    },

    onSetProjectTitle: title => {
        dispatch(setProjectTitle(title));
    },

    requestProjectUpload: loadingState => {
        dispatch(requestProjectUpload(loadingState));
    }
});

    const mergeProps = (
        stateProps,
        dispatchProps,
        ownProps
    ) => Object.assign(
        {},
        stateProps,
        dispatchProps,
        ownProps
    );

    return injectIntl(connect(
        mapStateToProps,
        mapDispatchToProps,
        mergeProps
    )(SBFileUploaderComponent));
};

export {
    SBFileUploaderHOC as default
};